import { useEffect, useMemo, useRef, useState } from "react";

import {
  fetchRepositoryActivity,
  type RepositoryActivityResponse,
  type WeeklyPlannerRow,
} from "@/lib/api";
import type { PanelCommit, PanelPullRequest } from "@/components/planner-repository-activity";

export function useRepositoryActivity(openRowData: WeeklyPlannerRow | undefined) {
  const repositoryActivityCacheRef = useRef(new Map<string, RepositoryActivityResponse>());
  const repositoryLinksFingerprint = useMemo(() => {
    if (!openRowData || openRowData.row_kind !== "project") return "";
    return (openRowData.repository_links || [])
      .map((r) => `${r.provider}:${r.url ?? ""}`)
      .join("|");
  }, [openRowData?.row_kind, openRowData?.repository_links]);

  const [panelRepositoryActivity, setPanelRepositoryActivity] = useState<{
    loading: boolean;
    error: string | null;
    pullRequests: PanelPullRequest[];
    commits: PanelCommit[];
  } | null>(null);

  useEffect(() => {
    if (!openRowData || openRowData.row_kind !== "project") {
      setPanelRepositoryActivity(null);
      return;
    }
    const links = openRowData.repository_links || [];
    if (links.length === 0) {
      setPanelRepositoryActivity({ loading: false, error: null, pullRequests: [], commits: [] });
      return;
    }

    let cancelled = false;
    setPanelRepositoryActivity({ loading: true, error: null, pullRequests: [], commits: [] });

    const cache = repositoryActivityCacheRef.current;

    void (async () => {
      try {
        const results = await Promise.all(
          links.map(async (repo, repositoryIndex) => {
            const url = repo.url?.trim();
            if (!url) {
              return {
                repositoryIndex,
                repo,
                data: { pull_requests: [], commits: [] } satisfies RepositoryActivityResponse,
              };
            }
            const key = `${repo.provider}:${url}`;
            let data = cache.get(key);
            if (!data) {
              data = await fetchRepositoryActivity(repo.provider, url);
              cache.set(key, data);
            }
            return { repositoryIndex, repo, data };
          }),
        );

        if (cancelled) return;

        const pullRequests: PanelPullRequest[] = [];
        const commits: PanelCommit[] = [];
        for (const { repositoryIndex, repo, data } of results) {
          (data.pull_requests || []).forEach((pullRequest, pullRequestIndex) => {
            pullRequests.push({
              key: `pr-${repositoryIndex}-${pullRequestIndex}`,
              repoName: repo.name || "Unknown repo",
              title: pullRequest.title,
              description: pullRequest.description,
              url: pullRequest.url,
              status: pullRequest.status,
              updated_at: pullRequest.updated_at,
            });
          });
          (data.commits || []).forEach((commit, commitIndex) => {
            commits.push({
              key: `commit-${repositoryIndex}-${commitIndex}`,
              repoName: repo.name || "Unknown repo",
              title: commit.title,
              description: commit.description,
              url: commit.url,
              author: commit.author,
              committed_at: commit.committed_at,
            });
          });
        }
        setPanelRepositoryActivity({ loading: false, error: null, pullRequests, commits });
      } catch (error) {
        if (cancelled) return;
        setPanelRepositoryActivity({
          loading: false,
          error: error instanceof Error ? error.message : "Failed to load repository activity",
          pullRequests: [],
          commits: [],
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [openRowData?.row_kind, openRowData?.row_entity_id, repositoryLinksFingerprint]);

  const panelPullRequests = panelRepositoryActivity?.pullRequests ?? [];
  const panelCommits = panelRepositoryActivity?.commits ?? [];
  const repositoryActivityLoading = Boolean(
    openRowData?.row_kind === "project" &&
    (openRowData.repository_links || []).length > 0 &&
    (panelRepositoryActivity === null || panelRepositoryActivity.loading),
  );
  const repositoryActivityError = panelRepositoryActivity?.error ?? null;

  return {
    panelPullRequests,
    panelCommits,
    repositoryActivityLoading,
    repositoryActivityError,
  };
}
